/**
 * IdleDimOverlay — the visual half of #138's bedside dim.
 *
 * Day mode:
 *   - 72% black overlay
 *   - any tap wakes
 *
 * Night mode:
 *   - 97% black overlay (essentially off)
 *   - soft clock displayed in the centre (low-brightness white)
 *   - hint text at the bottom
 *   - wake requires a deliberate gesture: long-press (>=500ms) OR
 *     double-tap (within 400ms). A single short tap does NOT wake.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';

// #441 Phase 2 — wake-hint i18n bucket.
function _useNightWakeHint() {
  const ctx = useLang();
  const s = ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.idledim;
  return (s && s.nightWakeHint) || 'Long-press or double-tap to wake';
}

const LONG_PRESS_MS = 500;
const DOUBLE_TAP_MS = 400;
const TOUCH_MOVE_TOLERANCE_PX = 10;

export default function IdleDimOverlay({ dimmed, config, onWake }) {
  const nightWakeHint = _useNightWakeHint();
  const [now, setNow] = useState(() => new Date());
  const lastTapRef = useRef(0);
  const longPressTimerRef = useRef(null);
  const touchStartXYRef = useRef(null);

  // Tick the clock once per minute, aligned to the next :00 second
  useEffect(() => {
    if (!dimmed || !config.showClock) return;
    let intervalId = null;
    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    const timeoutId = setTimeout(() => {
      setNow(new Date());
      intervalId = setInterval(() => setNow(new Date()), 60_000);
    }, msToNextMinute);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [dimmed, config.showClock]);

  if (!dimmed) return null;

  const isNight = config.wakeGesture === 'longpress-or-doubletap';

  // ── Wake gesture handlers ─────────────────────────────────────────────────
  function handleTouchStart(e) {
    if (config.wakeGesture === 'tap') return;  // day mode handled by onClick
    const t = e.touches && e.touches[0];
    touchStartXYRef.current = t ? { x: t.clientX, y: t.clientY } : null;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      onWake();
    }, LONG_PRESS_MS);
  }

  function handleTouchMove(e) {
    if (config.wakeGesture === 'tap') return;
    const start = touchStartXYRef.current;
    if (!start) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (dx * dx + dy * dy > TOUCH_MOVE_TOLERANCE_PX * TOUCH_MOVE_TOLERANCE_PX) {
      // The user is dragging, not pressing — cancel the long-press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  }

  function handleTouchEnd() {
    if (config.wakeGesture === 'tap') return;
    if (longPressTimerRef.current) {
      // Touch ended before LONG_PRESS_MS — not a long-press; check double-tap
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      const now = Date.now();
      if (now - lastTapRef.current <= DOUBLE_TAP_MS) {
        lastTapRef.current = 0;
        onWake();
      } else {
        lastTapRef.current = now;
      }
    }
  }

  // Day mode: any click wakes
  const onClick = config.wakeGesture === 'tap' ? onWake : undefined;

  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const clockText = `${hh}:${mm}`;

  return (
    <div
      onClick={onClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        background: `rgba(0, 0, 0, ${config.overlayOpacity})`,
        zIndex: 9999,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {config.showClock && (
        <div
          style={{
            color: 'rgba(255, 255, 255, 0.30)',
            fontSize: 'clamp(48px, 12vw, 96px)',
            fontWeight: 200,
            letterSpacing: '0.05em',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {clockText}
        </div>
      )}
      {isNight && (
        <div
          style={{
            position: 'absolute',
            bottom: '8vh',
            color: 'rgba(255, 255, 255, 0.20)',
            fontSize: '0.85rem',
            letterSpacing: '0.05em',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {nightWakeHint}
        </div>
      )}
    </div>
  );
}
